import { defaultContentSettings } from '../reducers/content/settings';
import { defaultSharingStudents } from '../reducers/content/sharing_students';
import { isInstructorInSection } from './current_user.js';
import R from 'ramda';
import { createSelector } from 'reselect';
import deprecated from '../deprecated';

import { param } from './utils';

// This is a NOOP, but it's here to be more explicit about the fact that we
// don't want selectContentStoreRoot
const selectRoot = root => root;

const selectContentStoreRoot = store => {
    return store.content;
};

const selectContentTree = createSelector(
    selectContentStoreRoot,
    store => store.content
);

const selectAllContent = createSelector(selectContentTree, R.values);

// (root, id) => content
export const selectContent = createSelector(
    selectContentTree,
    param(1),
    (tree, id) => tree[id]
);
export const getContent = deprecated(
    selectContent,
    `Use selectContent instead`
);

// (state, section) => [ ...content ]
export const selectContentForSection = createSelector(
    selectAllContent,
    param(1),
    (allContent, section) => {
        if (!section) {
            throw new Error(`missing Section`);
        }
        const sameSection = content => {
            // eslint-disable-next-line
            return content.section_id == section.id;
        };

        return R.filter(sameSection, allContent).sort(({ title }, contentB) => {
            return title.localeCompare(contentB.title);
        });
    }
);

// (state, section) => [ ...content ]
export const getSortedContentForSection = createSelector(
    selectContentForSection,
    R.sortBy(R.prop('title'))
);

const notEmptyString = s => s !== '';
const notInCollection = content =>
    content.tags.filter(notEmptyString).length === 0;

// (state, section) => [ ...content ]
export const selectNonCollectionContentForSection = createSelector(
    selectContentForSection,
    (state, section) => isInstructorInSection(state, section),
    (content, isInstructor) => {
        const finalized = content => content.is_finalized || isInstructor;
        return content.filter(finalized).filter(notInCollection);
    }
);

const selectSettingsRoot = createSelector(
    selectContentStoreRoot,
    store => store.contentSettings
);

// (root, content) => contentSettings
const selectSettingsForContent = createSelector(
    selectSettingsRoot,
    (_, content) => content.id,
    (settings, id) => R.propOr(defaultContentSettings, id, settings)
);

// (root, content) => number
export const selectZoomScale = createSelector(
    selectSettingsForContent,
    settings =>
        matchMedia('screen and (max-width: 768px)').matches ? 1 : settings.scale
);
export const getZoomScale = deprecated(
    selectZoomScale,
    `use selectZoomScale instead`
);

// (root, content) => ?
export const selectScaleCenter = createSelector(
    selectSettingsForContent,
    settings => settings.scaleCenter
);
export const getScaleCenter = deprecated(
    selectScaleCenter,
    `use selectScaleCenter instead`
);

// (root, content) => boolean
export const selectShowOthersHighlights = createSelector(
    selectSettingsForContent,
    settings => settings.showOthersHighlights
);
export const getShowOthersHighlights = deprecated(
    selectShowOthersHighlights,
    `use selectShowOthersHighlights instead`
);

export function getPageRange(_, content, first_pagenum, last_pagenum) {
    return content.pages.filter(
        p => first_pagenum <= p.pagenum && p.pagenum <= last_pagenum
    );
}

// (root, content) => boolean
export const hasSharing = createSelector(
    selectContentStoreRoot,
    (_, content) => content.id,
    (state, id) => R.has(id, state.sharing)
);

// (root, content) => boolean
export const hasSharingStudents = createSelector(
    selectContentStoreRoot,
    param(1),
    (state, content) => {
        const key = `/api/v3/section/${content.section_id}/`;
        return R.has(key, state.sharingStudents);
    }
);

export const getSharingStudents = createSelector(
    selectContentStoreRoot,
    param(1),
    (state, content) => {
        const key = `/api/v3/section/${content.section_id}/`;

        return R.propOr(defaultSharingStudents, key, state.sharingStudents);
    }
);

// (root, content) => string
export const selectCoverImage = createSelector(
    selectRoot,
    param(1),
    (root, content) => {
        const cover_override = content.link_cover_override;
        if (cover_override != null) {
            const [url] = cover_override.split('?');
            const [
                protocol,
                empty, // eslint-disable-line no-unused-vars
                ...rest
            ] = url.split('/');

            // TODO 8-8-22 stripping leading whitespace
            // This shouldn't have to be handled here, and a long term solution should involve
            // enforcing no white space at insertion
            switch (protocol.replace(/^\s+/g, '')) {
                case 'http:':
                case 'https:':
                    // TODO 8-8-22 bandaid to address whitespace in http/https cover links
                    return new URL(cover_override).href;
                case 'youtube:':
                    return `http://img.youtube.com/vi/${rest[0]}/0.jpg`;
                case 'vimeo:': {
                    return undefined;
                }
                default:
            }
        }
    }
);



// WEBPACK FOOTER //
// ./src/selectors/content.js